// Per-clip orchestrator for the Flex flow: recorded Blob -> pose frames ->
// FlexFinding[]. Mirrors analysis/pipeline.ts's analyzeVideo() orchestration
// pattern, but deliberately simpler — a held/active movement clip needs no
// gait-cycle detection, walking-bout trimming, or view-mismatch advisory.
//
// Pose source stays single: ../pose/poseEngine.MediaPipePoseEngine — same
// model as gait (see flex/types.ts CURRENT_FLEX_VERSIONS.poseModel).

import { MediaPipePoseEngine } from "../pose/poseEngine";
import { loadVideoFromBlob } from "../capture/video";
import { computeFlexFindings } from "./pipeline";
import type { FlexFinding } from "./types";

export interface FlexClipResult {
  findings: FlexFinding[];
  frameCount: number;
  durationMs: number;
}

export async function processFlexClip(
  blob: Blob,
  captureGroup: string,
  onProgress?: (fraction: number) => void,
): Promise<FlexClipResult> {
  const { video, url } = await loadVideoFromBlob(blob);
  const aspect =
    video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : 16 / 9;
  const engine = new MediaPipePoseEngine();
  try {
    await engine.init({ variant: "full" });
    const frames = await engine.processVideo(video, { onProgress });
    const findings = computeFlexFindings(captureGroup, frames, aspect);
    const durationMs = isFinite(video.duration) ? video.duration * 1000 : 0;
    return { findings, frameCount: frames.length, durationMs };
  } finally {
    engine.close();
    URL.revokeObjectURL(url);
  }
}
